import { useDebounce } from "@/hooks/use-debounce";
import React from "react";
import { Input, InputProps } from "../ui/input";
import { IsEqual } from "type-fest";
import { TextArea, TextAreaProps } from "../ui/textarea";
import { clamp } from "@/lib/utils";

type Defined<T> = T extends infer K | undefined
  ? K
  : T extends infer K | null
    ? K
    : T;

type Ev = React.MouseEvent | React.KeyboardEvent | React.ChangeEvent;

export const CLEAR = Symbol("clear");
type OnChangeReturn = any | typeof CLEAR;

type DebouncedInputAny<Type, Stored, Element extends HTMLElement> = {
  value: Type;
  onChange: (value: Type, ev?: Ev) => OnChangeReturn;
  onPending?: (value?: Type, ev?: Ev) => void;
  backward?: (v: Stored) => Type;
  forward?: (value: Type) => Stored;
  get?: (el: Element) => Stored;
  set?: (el: Element, value: Stored) => void;
  validate?: (value: Type) => boolean;
};

type DebouncedExclude<Type, Stored, Element extends HTMLElement> =
  | (DebouncedInputAny<Exclude<Type, null | undefined>, Stored, Element> & {
      isUndefinable?: false;
      isNullable?: false;
    })
  | (DebouncedInputAny<
      Exclude<Type, null | undefined> | undefined,
      Stored,
      Element
    > & {
      isUndefinable: true;
      isNullable?: false;
      value?: Type | undefined;
    })
  | (DebouncedInputAny<
      Exclude<Type, null | undefined> | null,
      Stored,
      Element
    > & {
      isUndefinable?: false;
      isNullable: true;
    })
  | (DebouncedInputAny<
      Exclude<Type, null | undefined> | null | undefined,
      Stored,
      Element
    > & {
      isUndefinable: true;
      isNullable: true;
      value?: Type | null | undefined;
    });

type DebouncedInputString<Element extends HTMLElement> = DebouncedExclude<
  string,
  string,
  Element
> & { type: "string" };

type DebouncedInputCheckbox<Element extends HTMLElement> = DebouncedExclude<
  boolean,
  boolean,
  Element
> & { type: "checkbox" };

type DebouncedInputNumber<Element extends HTMLElement> = DebouncedExclude<
  number,
  string,
  Element
> & { type: "number" };

type TDebouncedTextArea<Type, Element extends HTMLElement> = DebouncedExclude<
  Type,
  string,
  Element
>;

export type InferDebouncedInputString<T> =
  IsEqual<T, string | null | undefined> extends true
    ? { type: "string"; isNullable: true; isUndefinable: true }
    : IsEqual<T, string | undefined> extends true
      ? { type: "string"; isNullable?: false; isUndefinable: true }
      : IsEqual<T, string | null> extends true
        ? { type: "string"; isNullable: true; isUndefinable?: false }
        : IsEqual<T, string> extends true
          ? {
              type: "string";
              isNullable?: false;
              isUndefinable?: false;
            }
          : never;
export type InferDebouncedInputNumber<T> =
  IsEqual<T, number | null | undefined> extends true
    ? { type: "number"; isNullable: true; isUndefinable: true }
    : IsEqual<T, number | undefined> extends true
      ? { type: "number"; isNullable?: false; isUndefinable: true }
      : IsEqual<T, number | null> extends true
        ? { type: "number"; isNullable: true; isUndefinable?: false }
        : IsEqual<T, number> extends true
          ? {
              type: "number";
              isNullable?: false;
              isUndefinable?: false;
            }
          : never;
export type InferDebouncedInputBoolean<T> =
  IsEqual<T, boolean | null | undefined> extends true
    ? { type: "checkbox"; isNullable: true; isUndefinable: true }
    : IsEqual<T, boolean | undefined> extends true
      ? { type: "checkbox"; isNullable?: false; isUndefinable: true }
      : IsEqual<T, boolean | null> extends true
        ? { type: "checkbox"; isNullable: true; isUndefinable?: false }
        : IsEqual<T, boolean> extends true
          ? {
              type: "checkbox";
              isNullable?: false;
              isUndefinable?: false;
            }
          : never;

export type InferDebouncedInput<T> =
  InferDebouncedInputString<T> extends never
    ? InferDebouncedInputBoolean<T> extends never
      ? InferDebouncedInputNumber<T> extends never
        ? {}
        : InferDebouncedInputNumber<T>
      : InferDebouncedInputBoolean<T>
    : InferDebouncedInputString<T>;

export type DebouncedProps<Element extends HTMLElement> = {
  debounce?: number;
} & (
  | DebouncedInputString<Element>
  | DebouncedInputCheckbox<Element>
  | DebouncedInputNumber<Element>
);

export type DebouncedInputProps = Omit<
  InputProps,
  "value" | "checked" | "onChange"
> &
  DebouncedProps<HTMLInputElement>;

export const DebouncedInput = React.forwardRef<
  HTMLInputElement,
  DebouncedInputProps
>(
  (
    {
      type,
      isUndefinable,
      isNullable,
      forward: _forward,
      backward: _backward,
      set: _set,
      get: _get,
      debounce: _debounce,
      value,
      onChange,
      onPending,
      validate,
      ...props
    },
    _ref,
  ) => {
    const get: Defined<typeof _get> = React.useMemo(
      () =>
        _get ??
        (type === "checkbox"
          ? (el) => el.checked
          : type === "number"
            ? (el) => el.value
            : // type === "string"
              (el) => el.value),
      [_get, type],
    );

    const set: Defined<typeof _set> = React.useMemo(
      () =>
        _set ??
        (type === "checkbox"
          ? (el, value) => (el.checked = value)
          : type === "number"
            ? (el, value) => (el.value = value)
            : // type === "string"
              (el, value) => (el.value = value)),
      [_set, type],
    );

    const backward: Defined<typeof _backward> = React.useMemo(
      () =>
        _backward ??
        (type === "checkbox"
          ? (v: boolean) => v
          : type === "number"
            ? isUndefinable
              ? (v: string) => (!v ? undefined : +v)
              : isNullable
                ? (v: string) => (!v ? null : +v)
                : (v: string) => +v
            : // type === "string"
              isUndefinable
              ? (v: string) => v || undefined
              : isNullable
                ? (v: string) => v || null
                : (v: string) => v),
      [_backward, type, isUndefinable, isNullable],
    );

    const forward: Defined<typeof _forward> = React.useMemo(
      () =>
        _forward ??
        (type === "checkbox"
          ? (v: boolean) => v
          : type === "number"
            ? (v: number) => v as unknown as string
            : // type === "string"
              isUndefinable
              ? (v: string | undefined) => v || ""
              : isNullable
                ? (v: string | null) => v || ""
                : (v: string) => v),
      [_forward, type, isUndefinable, isNullable],
    );

    const [retain, setRetain] = React.useState<boolean>(true);

    const ref = React.useRef<HTMLInputElement | null>(null);

    React.useMemo(() => {
      if (ref.current && retain) {
        (set as any)(ref.current, (forward as any)(value));
      }
    }, [value, set, forward, retain]);

    const update = (el: HTMLInputElement, ev: Ev) => {
      const v = (backward as any)(get(el));
      if (validate) {
        const valid = (validate as any)(v);
        if (!valid) {
          return;
        }
      }
      if (onPending) onPending(undefined);
      const ret = (onChange as any)(v, ev);
      if (ret === CLEAR && ref.current) {
        (set as any)(ref.current, (forward as any)(value));
      }
    };
    const store = (el: HTMLInputElement, ev: Ev) => {
      const value = (backward as any)(get(el));
      if (onPending) onPending(value);
    };
    const debounce = useDebounce();
    const now = (el: HTMLInputElement, ev: Ev) => {
      debounce.cancel();
      update(el, ev);
    };
    const delay = (el: HTMLInputElement, ev: Ev) => {
      if (!_debounce || validate) {
        return now(el, ev);
      }
      if (_debounce < 0) {
        return store(el, ev);
      }
      setRetain(false);
      debounce(_debounce, () => {
        update(el, ev);
        setRetain(true);
      });
    };

    return (
      <Input
        {...props}
        ref={(el) => {
          ref.current = el;
          if (typeof _ref === "function") {
            _ref(el);
          } else if (_ref) {
            _ref.current = el;
          }
        }}
        defaultValue={
          type !== "checkbox" ? (forward as any)(value as any) : undefined
        }
        defaultChecked={
          type === "checkbox" ? (forward as any)(value as any) : undefined
        }
        onChange={(ev) => delay(ev.target, ev)}
        type={type}
        onKeyDown={(ev) => {
          if (props?.onKeyDown) {
            props?.onKeyDown(ev);
          }
          if (ev.key === "Enter") {
            now(ev.currentTarget, ev);
          }
        }}
        onBlur={(ev) => {
          now(ev.target, ev);
        }}
      />
    );
  },
);

export type DebouncedTextAreaProps<Type = any> = Omit<
  TextAreaProps,
  "value" | "checked" | "onChange" | "rows"
> & { debounce?: number } & TDebouncedTextArea<Type, HTMLTextAreaElement> & {
    rows?: number | "auto";
    getRows?: (v: string) => number;
    minRows?: number;
    maxRows?: number;
  };

export const DebouncedTextArea = React.forwardRef<
  HTMLTextAreaElement,
  DebouncedTextAreaProps
>(
  (
    {
      isUndefinable,
      isNullable,
      forward: _forward,
      backward: _backward,
      set: _set,
      get: _get,
      debounce: _debounce,
      value,
      onChange,
      onPending,
      validate,
      rows,
      getRows: _getRows,
      minRows,
      maxRows,
      ...props
    },
    _ref,
  ) => {
    const get: Defined<typeof _get> = React.useMemo(
      () => _get ?? ((el) => el.value),
      [_get],
    );

    const set: Defined<typeof _set> = React.useMemo(
      () => _set ?? ((el, value) => (el.value = value)),
      [_set],
    );

    const backward: Defined<typeof _backward> = React.useMemo(
      () =>
        _backward ??
        (isUndefinable
          ? (v: string) => v || undefined
          : isNullable
            ? (v: string) => v || null
            : (v: string) => v),
      [_backward, isUndefinable, isNullable],
    );

    const getRows: Defined<typeof _getRows> = React.useMemo(
      () => _getRows ?? ((v) => v.split("\n").length),
      [_getRows],
    );

    const forward: Defined<typeof _forward> = React.useMemo(
      () =>
        _forward ??
        (isUndefinable
          ? (v: string | undefined) => v || ""
          : isNullable
            ? (v: string | null) => v || ""
            : (v: string) => v),
      [_forward, isUndefinable, isNullable],
    );

    const [retain, setRetain] = React.useState<boolean>(true);

    const ref = React.useRef<HTMLTextAreaElement | null>(null);
    const rowsRef = React.useRef<number | undefined>(
      rows !== "auto" ? rows : undefined,
    );

    const autoRows = rows === "auto";
    const applyRows = React.useCallback(
      (element: HTMLTextAreaElement, value: string) => {
        if (autoRows) {
          const prevRows = rowsRef.current;
          const nextRows = clamp(getRows(value), minRows ?? 1, maxRows);
          if (prevRows != nextRows) {
            (element as HTMLTextAreaElement).rows = nextRows;
          }
          rowsRef.current = nextRows;
        }
      },
      [autoRows, getRows],
    );

    React.useMemo(() => {
      if (ref.current && retain) {
        const next = forward(value);
        applyRows(ref.current, next);
        set(ref.current, next);
      }
    }, [value, set, forward, retain, applyRows]);

    const update = (el: HTMLTextAreaElement, ev: Ev) => {
      const value = backward(get(el));
      if (validate) {
        const valid = validate(value);
        if (!valid) {
          return;
        }
      }
      if (onPending) onPending(undefined);
      onChange(value, ev);
    };
    const store = (el: HTMLTextAreaElement, ev: Ev) => {
      try {
        const value = backward(get(el));
        if (onPending) onPending(value);
      } catch (ex) {
        //
      }
    };
    const debounce = useDebounce();
    const now = (el: HTMLTextAreaElement, ev: Ev) => {
      debounce.cancel();
      update(el, ev);
    };
    const delay = (el: HTMLTextAreaElement, ev: Ev) => {
      if (!_debounce || validate) {
        return now(el, ev);
      }
      if (_debounce < 0) {
        return store(el, ev);
      }
      setRetain(false);
      debounce(_debounce, () => {
        update(el, ev);
        setRetain(true);
      });
    };

    return (
      <TextArea
        {...props}
        rows={rows !== "auto" ? rows : undefined}
        ref={(el) => {
          ref.current = el;
          if (el) {
            applyRows(el, get(el));
          }
          if (typeof _ref === "function") {
            _ref(el);
          } else if (_ref) {
            _ref.current = el;
          }
        }}
        defaultValue={forward(value)}
        onChange={(ev) => {
          applyRows(
            ev.target as HTMLTextAreaElement,
            get(ev.target as HTMLTextAreaElement),
          );
          return delay(ev.target as HTMLTextAreaElement, ev as unknown as Ev);
        }}
        onKeyDown={(ev) => {
          if (props?.onKeyDown) {
            props?.onKeyDown(ev);
          }
          if (ev.ctrlKey && ev.key === "Enter") {
            now(ev.currentTarget, ev);
          }
        }}
        onBlur={(ev) => {
          now(ev.target, ev);
        }}
      />
    );
  },
);
